'use client';

import type { Dispatch, ReactNode } from 'react';
import { createContext, useMemo, useState, useEffect, useCallback, useReducer } from 'react';
import { BOOKABLE_ENTITY, WIDGET_BUTTON_STATES, WIDGET_MESSAGE_STATES } from '@/constants/ibkConstants';
import { getPayloadForFetchingIbkAvailability } from '@/utils/ibk/getIbkAvailabilityPayload';
import type { ShoppingCartItem } from '@/models/ShoppingCartItem';
import type { IbkData } from '@/models/IbkData';
import type { IbkAction } from '@/models/IbkAction';
import { loadDateFnsFunctions } from '@/utils/dateHelper';

export type IbkContextType = {
  IBK_DATA: {
    ibkState: IbkData;
    preFilledFilters: {
      preFilledMeetingSpaceAttendees?: number;
      preFilledStartDate?: Date;
      preFilledEndDate?: Date;
      preFilledGuestRoomCount?: number;
      preFilledGuestRoomStartDate?: Date;
      preFilledGuestRoomEndDate?: Date;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dateFnsFunctions: any;
    configSkuJWT: string;
  };
  IBK_ACTIONS: {
    ibkDispatch: Dispatch<IbkAction>;
    getAvailability: () => void;
  };
};

type IbkAvailabilityProps = {
  venueId: string;
  geoLocation: {
    lat: number;
    lon: number;
  };
  preFilledMeetingSpaceAttendees: number | undefined;
  preFilledGuestRoomCount: number | undefined;
  preFilledStartDate: Date | undefined;
  preFilledEndDate: Date | undefined;
  preFilledGuestRoomStartDate: Date | undefined;
  preFilledGuestRoomEndDate: Date | undefined;
  rfpSupplierIds: Array<ShoppingCartItem>;
  configSkuJWT: string;
  children: ReactNode;
};

export type MeetingRoomSelection = {
  meetingRoomCount?: number;
  meetingRoomStartDate?: Date;
  meetingRoomEndDate?: Date;
};

export type GuestRoomSelection = {
  guestRoomCount?: number;
  guestRoomStartDate?: Date;
  guestRoomEndDate?: Date;
};

export type RoomDates = {
  startDate?: Date;
  endDate?: Date;
};

const MEETING_ROOM_FIELD_IDS = {
  ATTENDEES: 'meeting-room-attendees',
  DATES: 'meeting-room-dates'
};

const GUEST_ROOM_FIELD_IDS = {
  ROOM_COUNT: 'guest-room-attendees',
  DATES: 'guest-room-dates'
};

export const IbkContext = createContext({} as IbkContextType);

function ibkReducer(state: IbkData, action: IbkAction): IbkData {
  switch (action.type) {
    case 'SET_INTEGRATED_ENTITY':
      return { ...state, integratedEntity: action.ibkPayload.integratedEntity };
    case 'SET_PRESELECTED_ENTITY':
      return { ...state, preSelectedEntity: action.ibkPayload.preSelectedEntity };
    case 'SET_SEARCH_PARAMS':
      return { ...state, searchParams: action.ibkPayload.searchParams };
    case 'SET_SEARCHED_FROM_MODAL':
      return { ...state, searchedFromModal: action.ibkPayload.searchedFromModal };
    case 'SET_BUTTON_STATE':
      return { ...state, buttonState: action.ibkPayload.buttonState };
    case 'SET_BANNER_MESSAGE':
      return { ...state, bannerMessageState: action.ibkPayload.bannerMessageState };
    case 'SET_PRE_POP_MEETING_ROOM_SELECTION':
      return { ...state, prepopMeetingRoomSelection: action.ibkPayload.prepopMeetingRoomSelection };
    case 'SET_PRE_POP_GUEST_ROOM_SELECTION':
      return { ...state, prepopGuestRoomSelection: action.ibkPayload.prepopGuestRoomSelection };
    case 'SET_RFP_WARNING':
      return { ...state, rfpWarning: action.ibkPayload.rfpWarning };
    case 'SET_IS_BOOKABLE_NOW':
      return { ...state, isBookableNow: action.ibkPayload.isBookableNow };
    case 'ADD_MEETING_ROOMS':
      return { ...state, addMeetingRooms: action.ibkPayload.addMeetingRooms };
    case 'ADD_SLEEPING_ROOMS':
      return { ...state, addSleepingRooms: action.ibkPayload.addSleepingRooms };
    case 'SET_IS_MODAL_OPEN':
      return { ...state, isModalOpen: action.ibkPayload.isModalOpen };
    case 'SET_CURRENCY_CODE':
      return { ...state, currencyCode: action.ibkPayload.currencyCode };
    case 'SET_MEETING_ROOM_RATE':
      return { ...state, meetingRoomRate: action.ibkPayload.meetingRoomRate };
    case 'SET_GUEST_ROOM_RATE':
      return { ...state, guestRoomRate: action.ibkPayload.guestRoomRate };
    case 'SET_SELECTIONS':
      return { ...state, selections: action.ibkPayload.selections };
    case 'SET_HIDE_WIDGET':
      return { ...state, hideWidget: action.ibkPayload.hideWidget };
    default:
      throw new Error();
  }
}

export default function IbkProvider({
  venueId,
  geoLocation,
  preFilledMeetingSpaceAttendees,
  preFilledGuestRoomCount,
  preFilledStartDate,
  preFilledEndDate,
  preFilledGuestRoomStartDate,
  preFilledGuestRoomEndDate,
  rfpSupplierIds,
  configSkuJWT,
  children
}: IbkAvailabilityProps) {
  const initialState: IbkData = useMemo(() => {
    const ibkInitialData = {
      integratedEntity: '',
      preSelectedEntity: '',
      searchParams: {},
      searchedFromModal: false,
      buttonState: WIDGET_BUTTON_STATES.DEFAULT,
      bannerMessageState: WIDGET_MESSAGE_STATES.NO_MESSAGE,
      prepopMeetingRoomSelection: {},
      prepopGuestRoomSelection: {},
      rfpWarning: rfpSupplierIds?.some(supplier => supplier.venueId === venueId),
      isBookableNow: false,
      addMeetingRooms: false,
      addSleepingRooms: false,
      isModalOpen: false,
      currencyCode: 'USD',
      meetingRoomRate: 0,
      guestRoomRate: 0,
      selections: {},
      hideWidget: false
    };
    return ibkInitialData;
  }, [rfpSupplierIds, venueId]);

  const [ibkState, ibkDispatch] = useReducer(ibkReducer, initialState);
  const { integratedEntity, isBookableNow, isModalOpen, searchedFromModal, searchParams, hideWidget } = ibkState;
  const [isLoading, setIsloading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dateFnsFunctions, setDateFnsFunctions] = useState<any>(null);

  // Pre-Filled filters
  const preFilledDatesValid = !!(preFilledStartDate && preFilledEndDate);
  const preFilledGuestDatesValid = !!(preFilledGuestRoomStartDate && preFilledGuestRoomEndDate);

  let meetingRoomCount = preFilledMeetingSpaceAttendees;
  let guestRoomCount = preFilledGuestRoomCount;

  if (preFilledDatesValid || preFilledGuestDatesValid) {
    // Dates are provided
    // use pre-determined count for initial availability check
    if (!meetingRoomCount) {
      meetingRoomCount = guestRoomCount || 1;
    }
    if (!guestRoomCount) {
      guestRoomCount = meetingRoomCount || 10;
    }
  }

  // Reset rates based on integrated flags
  useEffect(() => {
    if (integratedEntity === BOOKABLE_ENTITY.NONE) {
      ibkDispatch({ type: 'SET_MEETING_ROOM_RATE', ibkPayload: { meetingRoomRate: 0 } });
      ibkDispatch({ type: 'SET_GUEST_ROOM_RATE', ibkPayload: { guestRoomRate: 0 } });
      ibkDispatch({ type: 'SET_HIDE_WIDGET', ibkPayload: { hideWidget: true } });
    } else if (integratedEntity === BOOKABLE_ENTITY.MEETING_ROOM) {
      ibkDispatch({ type: 'SET_GUEST_ROOM_RATE', ibkPayload: { guestRoomRate: 0 } });
    } else if (integratedEntity === BOOKABLE_ENTITY.GUEST_ROOM) {
      ibkDispatch({ type: 'SET_MEETING_ROOM_RATE', ibkPayload: { meetingRoomRate: 0 } });
    }
  }, [integratedEntity]);

  const meetingRoomAttendeesFromWidgetModel = parseInt(searchParams?.[MEETING_ROOM_FIELD_IDS.ATTENDEES]?.value, 10);
  const meetingRoomDates: RoomDates = searchParams?.[MEETING_ROOM_FIELD_IDS.DATES]?.value;

  const guestRoomCountFromWidgetModal = parseInt(searchParams?.[GUEST_ROOM_FIELD_IDS.ROOM_COUNT]?.value, 10);
  const guestRoomDates: RoomDates = searchParams?.[GUEST_ROOM_FIELD_IDS.DATES]?.value;

  const isMeetingRoomFilledOut = !!(meetingRoomAttendeesFromWidgetModel && meetingRoomDates);
  const isGuestRoomFilledOut = !!(guestRoomCountFromWidgetModal && guestRoomDates);

  const preFilledFilters = useMemo(
    () => ({
      preFilledMeetingSpaceAttendees,
      preFilledStartDate,
      preFilledEndDate,
      preFilledGuestRoomCount,
      preFilledGuestRoomStartDate,
      preFilledGuestRoomEndDate
    }),
    [
      preFilledEndDate,
      preFilledGuestRoomCount,
      preFilledGuestRoomEndDate,
      preFilledGuestRoomStartDate,
      preFilledMeetingSpaceAttendees,
      preFilledStartDate
    ]
  );

  const onCompleted = useCallback(
    data => {
      const venue = data;
      const meetingRoomTotalCost = Array.isArray(venue?.meetingRooms)
        ? venue?.meetingRooms?.[0]?.totalCostBeforeTax
        : venue?.meetingRooms?.totalCostBeforeTax;
      const guestRoomLowestCost =
        venue.sleepingRooms?.[0].lowestAverageRateWithoutTax ?? venue.sleepingRooms?.[0].lowestAverageRateWithTax;
      const isIntegratedMeeting = venue?.meetingSpaceIntegration;
      const isIntegratedSleeping = venue?.sleepingSpaceIntegration;
      let currency;
      if (meetingRoomTotalCost || guestRoomLowestCost) {
        if (venue?.meetingRooms && venue?.meetingRooms?.length) {
          currency = venue.meetingRooms[0]?.currency;
        } else {
          currency = venue.sleepingRooms?.[0].currency;
        }
      }

      if (venue) {
        // Setting integrated flags
        let integratedSelection = '';
        if (!isIntegratedMeeting && !isIntegratedSleeping) {
          integratedSelection = BOOKABLE_ENTITY.NONE;
          if (!hideWidget) {
            ibkDispatch({ type: 'SET_HIDE_WIDGET', ibkPayload: { hideWidget: true } });
          }
        } else {
          if (hideWidget) {
            ibkDispatch({ type: 'SET_HIDE_WIDGET', ibkPayload: { hideWidget: false } });
          }
          if (isIntegratedMeeting && isIntegratedSleeping) {
            integratedSelection = BOOKABLE_ENTITY.BOTH;
          } else if (isIntegratedMeeting && !isIntegratedSleeping) {
            integratedSelection = BOOKABLE_ENTITY.MEETING_ROOM;
          } else if (!isIntegratedMeeting && isIntegratedSleeping) {
            integratedSelection = BOOKABLE_ENTITY.GUEST_ROOM;
          }
        }
        if (integratedSelection !== '') {
          ibkDispatch({
            type: 'SET_INTEGRATED_ENTITY',
            ibkPayload: {
              integratedEntity: integratedSelection
            }
          });
        }
        // Set currency code
        ibkDispatch({ type: 'SET_CURRENCY_CODE', ibkPayload: { currencyCode: currency } });

        // Setting Rates
        if (meetingRoomTotalCost && isIntegratedMeeting) {
          if (!searchedFromModal && preFilledDatesValid) {
            // Searched from prepop
            ibkDispatch({ type: 'SET_MEETING_ROOM_RATE', ibkPayload: { meetingRoomRate: meetingRoomTotalCost } });
            if (hideWidget) {
              ibkDispatch({ type: 'SET_HIDE_WIDGET', ibkPayload: { hideWidget: false } });
            }
          }
        }
        if (guestRoomLowestCost && isIntegratedSleeping) {
          if (!searchedFromModal && preFilledDatesValid) {
            ibkDispatch({ type: 'SET_GUEST_ROOM_RATE', ibkPayload: { guestRoomRate: guestRoomLowestCost } });
            if (hideWidget) {
              ibkDispatch({ type: 'SET_HIDE_WIDGET', ibkPayload: { hideWidget: false } });
            }
          }
        }

        // Setting prepop values and CTA button
        if (!meetingRoomTotalCost && !guestRoomLowestCost) {
          ibkDispatch({ type: 'SET_PRE_POP_GUEST_ROOM_SELECTION', ibkPayload: { prepopGuestRoomSelection: {} } });
          ibkDispatch({ type: 'SET_PRE_POP_MEETING_ROOM_SELECTION', ibkPayload: { prepopMeetingRoomSelection: {} } });
          ibkDispatch({ type: 'SET_IS_BOOKABLE_NOW', ibkPayload: { isBookableNow: false } });
        } else if (preFilledDatesValid) {
          if (!preFilledMeetingSpaceAttendees && !preFilledGuestRoomCount) {
            // No count is pre-filled
            if (isIntegratedMeeting && meetingRoomTotalCost) {
              ibkDispatch({
                type: 'SET_PRE_POP_MEETING_ROOM_SELECTION',
                ibkPayload: {
                  prepopMeetingRoomSelection: {
                    meetingRoomStartDate: preFilledStartDate,
                    meetingRoomEndDate: preFilledEndDate
                  }
                }
              });
            }
            if (isIntegratedSleeping && guestRoomLowestCost) {
              ibkDispatch({
                type: 'SET_PRE_POP_GUEST_ROOM_SELECTION',
                ibkPayload: {
                  prepopGuestRoomSelection: {
                    guestRoomStartDate: preFilledGuestRoomStartDate ?? preFilledStartDate,
                    guestRoomEndDate: preFilledGuestRoomEndDate ?? preFilledEndDate
                  }
                }
              });
            }
            if (guestRoomLowestCost && meetingRoomTotalCost) {
              ibkDispatch({
                type: 'SET_PRESELECTED_ENTITY',
                ibkPayload: {
                  preSelectedEntity: BOOKABLE_ENTITY.BOTH
                }
              });
            } else if (guestRoomLowestCost) {
              ibkDispatch({
                type: 'SET_PRESELECTED_ENTITY',
                ibkPayload: {
                  preSelectedEntity: BOOKABLE_ENTITY.GUEST_ROOM
                }
              });
            } else if (meetingRoomTotalCost) {
              ibkDispatch({
                type: 'SET_PRESELECTED_ENTITY',
                ibkPayload: {
                  preSelectedEntity: BOOKABLE_ENTITY.MEETING_ROOM
                }
              });
            } else {
              ibkDispatch({
                type: 'SET_PRESELECTED_ENTITY',
                ibkPayload: {
                  preSelectedEntity: BOOKABLE_ENTITY.NONE
                }
              });
            }
          } else if (preFilledMeetingSpaceAttendees && !preFilledGuestRoomCount) {
            // Only MeetingRoom count is pre-filled
            if (meetingRoomTotalCost && isIntegratedMeeting) {
              // MR Available = Book Now
              if (!isBookableNow && !searchedFromModal) {
                ibkDispatch({
                  type: 'SET_IS_BOOKABLE_NOW',
                  ibkPayload: {
                    isBookableNow: true
                  }
                });
              }
              ibkDispatch({
                type: 'ADD_MEETING_ROOMS',
                ibkPayload: {
                  addMeetingRooms: true
                }
              });
              ibkDispatch({
                type: 'ADD_SLEEPING_ROOMS',
                ibkPayload: {
                  addSleepingRooms: false
                }
              });
            } else if (guestRoomLowestCost && isIntegratedSleeping) {
              // only GR available - Check Availability + Preselect and prepop dates
              if (isBookableNow && !searchedFromModal) {
                ibkDispatch({
                  type: 'SET_IS_BOOKABLE_NOW',
                  ibkPayload: {
                    isBookableNow: false
                  }
                });
              }
              ibkDispatch({
                type: 'SET_PRESELECTED_ENTITY',
                ibkPayload: {
                  preSelectedEntity: BOOKABLE_ENTITY.GUEST_ROOM
                }
              });
              ibkDispatch({
                type: 'SET_PRE_POP_GUEST_ROOM_SELECTION',
                ibkPayload: {
                  prepopGuestRoomSelection: {
                    guestRoomStartDate: preFilledGuestRoomStartDate ?? preFilledStartDate,
                    guestRoomEndDate: preFilledGuestRoomEndDate ?? preFilledEndDate
                  }
                }
              });
            }
          } else if (!preFilledMeetingSpaceAttendees && preFilledGuestRoomCount) {
            // Only GuestRoom count is pre-filled
            if (guestRoomLowestCost && isIntegratedSleeping) {
              // GR Available = Book Now
              if (!isBookableNow && !searchedFromModal) {
                ibkDispatch({
                  type: 'SET_IS_BOOKABLE_NOW',
                  ibkPayload: {
                    isBookableNow: true
                  }
                });
              }
              ibkDispatch({
                type: 'ADD_SLEEPING_ROOMS',
                ibkPayload: {
                  addSleepingRooms: true
                }
              });
              ibkDispatch({
                type: 'ADD_MEETING_ROOMS',
                ibkPayload: {
                  addMeetingRooms: false
                }
              });
            } else if (meetingRoomTotalCost && isIntegratedMeeting) {
              // only MR available - Check Availability + Preselect and prepop dates
              if (isBookableNow && !searchedFromModal) {
                ibkDispatch({
                  type: 'SET_IS_BOOKABLE_NOW',
                  ibkPayload: {
                    isBookableNow: false
                  }
                });
              }
              ibkDispatch({
                type: 'SET_PRESELECTED_ENTITY',
                ibkPayload: {
                  preSelectedEntity: BOOKABLE_ENTITY.MEETING_ROOM
                }
              });
              ibkDispatch({
                type: 'SET_PRE_POP_MEETING_ROOM_SELECTION',
                ibkPayload: {
                  prepopMeetingRoomSelection: {
                    meetingRoomStartDate: preFilledStartDate,
                    meetingRoomEndDate: preFilledEndDate
                  }
                }
              });
            }
          } else if (preFilledMeetingSpaceAttendees && preFilledGuestRoomCount) {
            // Both counts pre-filled
            if (meetingRoomTotalCost && isIntegratedMeeting) {
              if (!isBookableNow && !searchedFromModal) {
                ibkDispatch({
                  type: 'SET_IS_BOOKABLE_NOW',
                  ibkPayload: {
                    isBookableNow: true
                  }
                });
              }
              ibkDispatch({
                type: 'ADD_MEETING_ROOMS',
                ibkPayload: {
                  addMeetingRooms: true
                }
              });
            }
            if (guestRoomLowestCost && isIntegratedSleeping) {
              if (!isBookableNow && !searchedFromModal) {
                ibkDispatch({
                  type: 'SET_IS_BOOKABLE_NOW',
                  ibkPayload: {
                    isBookableNow: true
                  }
                });
              }
              ibkDispatch({
                type: 'ADD_SLEEPING_ROOMS',
                ibkPayload: {
                  addSleepingRooms: true
                }
              });
            }
          }
        }
        // Setting button state (Filled out in modal):
        if (isMeetingRoomFilledOut && isGuestRoomFilledOut) {
          // Both filled out
          if (meetingRoomTotalCost && guestRoomLowestCost) {
            ibkDispatch({
              type: 'SET_BUTTON_STATE',
              ibkPayload: {
                buttonState: WIDGET_BUTTON_STATES.AVAILABLE
              }
            });
            ibkDispatch({
              type: 'SET_BANNER_MESSAGE',
              ibkPayload: {
                bannerMessageState: WIDGET_MESSAGE_STATES.INVENTORY_AVAILABLE
              }
            });
            ibkDispatch({
              type: 'ADD_MEETING_ROOMS',
              ibkPayload: {
                addMeetingRooms: true
              }
            });
            ibkDispatch({
              type: 'ADD_SLEEPING_ROOMS',
              ibkPayload: {
                addSleepingRooms: true
              }
            });
          } else {
            if (meetingRoomTotalCost) {
              ibkDispatch({
                type: 'SET_BANNER_MESSAGE',
                ibkPayload: {
                  bannerMessageState: WIDGET_MESSAGE_STATES.MEETING_ROOMS_ONLY
                }
              });
            } else if (guestRoomLowestCost) {
              ibkDispatch({
                type: 'SET_BANNER_MESSAGE',
                ibkPayload: {
                  bannerMessageState: WIDGET_MESSAGE_STATES.GUEST_ROOMS_ONLY
                }
              });
            } else {
              ibkDispatch({
                type: 'SET_BANNER_MESSAGE',
                ibkPayload: {
                  bannerMessageState: WIDGET_MESSAGE_STATES.INVENTORY_NOT_AVAILABLE
                }
              });
            }
            ibkDispatch({
              type: 'SET_BUTTON_STATE',
              ibkPayload: {
                buttonState: WIDGET_BUTTON_STATES.ERROR
              }
            });
          }
        } else if (isMeetingRoomFilledOut && !isGuestRoomFilledOut) {
          // Only meeting room filled out
          if (meetingRoomTotalCost) {
            ibkDispatch({
              type: 'SET_BUTTON_STATE',
              ibkPayload: {
                buttonState: WIDGET_BUTTON_STATES.AVAILABLE
              }
            });
            ibkDispatch({
              type: 'SET_BANNER_MESSAGE',
              ibkPayload: {
                bannerMessageState: WIDGET_MESSAGE_STATES.INVENTORY_AVAILABLE
              }
            });
            ibkDispatch({
              type: 'ADD_MEETING_ROOMS',
              ibkPayload: {
                addMeetingRooms: true
              }
            });
            ibkDispatch({
              type: 'ADD_SLEEPING_ROOMS',
              ibkPayload: {
                addSleepingRooms: false
              }
            });
          } else {
            ibkDispatch({
              type: 'SET_BUTTON_STATE',
              ibkPayload: {
                buttonState: WIDGET_BUTTON_STATES.ERROR
              }
            });
            ibkDispatch({
              type: 'SET_BANNER_MESSAGE',
              ibkPayload: {
                bannerMessageState: WIDGET_MESSAGE_STATES.INVENTORY_NOT_AVAILABLE
              }
            });
          }
        } else if (!isMeetingRoomFilledOut && isGuestRoomFilledOut) {
          // Only guest room filled out
          if (guestRoomLowestCost) {
            ibkDispatch({
              type: 'SET_BUTTON_STATE',
              ibkPayload: {
                buttonState: WIDGET_BUTTON_STATES.AVAILABLE
              }
            });
            ibkDispatch({
              type: 'SET_BANNER_MESSAGE',
              ibkPayload: {
                bannerMessageState: WIDGET_MESSAGE_STATES.INVENTORY_AVAILABLE
              }
            });
            ibkDispatch({
              type: 'ADD_SLEEPING_ROOMS',
              ibkPayload: {
                addSleepingRooms: true
              }
            });
            ibkDispatch({
              type: 'ADD_MEETING_ROOMS',
              ibkPayload: {
                addMeetingRooms: false
              }
            });
          } else {
            ibkDispatch({
              type: 'SET_BUTTON_STATE',
              ibkPayload: {
                buttonState: WIDGET_BUTTON_STATES.ERROR
              }
            });

            ibkDispatch({
              type: 'SET_BANNER_MESSAGE',
              ibkPayload: {
                bannerMessageState: WIDGET_MESSAGE_STATES.INVENTORY_NOT_AVAILABLE
              }
            });
          }
        } else {
          ibkDispatch({
            type: 'SET_BUTTON_STATE',
            ibkPayload: {
              buttonState: WIDGET_BUTTON_STATES.DEFAULT
            }
          });
          ibkDispatch({
            type: 'SET_BANNER_MESSAGE',
            ibkPayload: {
              bannerMessageState: WIDGET_MESSAGE_STATES.NO_MESSAGE
            }
          });
        }
      } else {
        ibkDispatch({
          type: 'SET_BUTTON_STATE',
          ibkPayload: {
            buttonState: WIDGET_BUTTON_STATES.ERROR
          }
        });
      }
    },
    [
      hideWidget,
      isBookableNow,
      isGuestRoomFilledOut,
      isMeetingRoomFilledOut,
      preFilledDatesValid,
      preFilledEndDate,
      preFilledGuestRoomCount,
      preFilledGuestRoomEndDate,
      preFilledGuestRoomStartDate,
      preFilledMeetingSpaceAttendees,
      preFilledStartDate,
      searchedFromModal
    ]
  );

  const onError = useCallback(() => {
    ibkDispatch({
      type: 'SET_BUTTON_STATE',
      ibkPayload: { buttonState: WIDGET_BUTTON_STATES.ERROR }
    });
  }, []);

  const getAvailability = useCallback(() => {
    setIsloading(true);
    async function fetchIbkAvailability() {
      const { format } = dateFnsFunctions;
      const payload = getPayloadForFetchingIbkAvailability({
        venueId,
        geoLocation,
        isGuestRoomFilledOut,
        preFilledDatesValid,
        searchedFromModal,
        preFilledStartDate,
        preFilledEndDate,
        guestRoomDates,
        meetingRoomDates,
        guestRoomCountFromWidgetModal,
        guestRoomCount,
        isMeetingRoomFilledOut,
        meetingRoomAttendeesFromWidgetModel,
        meetingRoomCount,
        format
      });
      ibkDispatch({
        type: 'SET_SELECTIONS',
        ibkPayload: {
          selections: payload.selections
        }
      });
      const response = await fetch('/venues/api/ibk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        const venueResponse = await response.json();
        onCompleted(venueResponse);
      } else {
        onError();
      }
      ibkDispatch({
        type: 'SET_SEARCHED_FROM_MODAL',
        ibkPayload: {
          searchedFromModal: false
        }
      });
      setIsloading(false);
    }
    fetchIbkAvailability();
  }, [
    dateFnsFunctions,
    venueId,
    geoLocation,
    isGuestRoomFilledOut,
    preFilledDatesValid,
    searchedFromModal,
    preFilledStartDate,
    preFilledEndDate,
    guestRoomDates,
    meetingRoomDates,
    guestRoomCountFromWidgetModal,
    guestRoomCount,
    isMeetingRoomFilledOut,
    meetingRoomAttendeesFromWidgetModel,
    meetingRoomCount,
    onCompleted,
    onError
  ]);

  useEffect(() => {
    if (isLoading) {
      ibkDispatch({
        type: 'SET_BUTTON_STATE',
        ibkPayload: {
          buttonState: WIDGET_BUTTON_STATES.LOADING
        }
      });
    }
  }, [isLoading]);

  useEffect(() => {
    let functions;
    async function loadFunctions() {
      functions = await loadDateFnsFunctions();
      setDateFnsFunctions(functions);
    }

    loadFunctions();
  }, []);

  // Always re-enable the RFP Warning when re-opening the modal
  useEffect(() => {
    if (isModalOpen && rfpSupplierIds?.some(supplier => supplier.venueId === venueId)) {
      ibkDispatch({
        type: 'SET_RFP_WARNING',
        ibkPayload: {
          rfpWarning: true
        }
      });
    }
  }, [isModalOpen, rfpSupplierIds, venueId]);

  const value = useMemo(
    () => ({
      IBK_DATA: {
        ibkState,
        preFilledFilters,
        dateFnsFunctions,
        configSkuJWT
      },
      IBK_ACTIONS: {
        ibkDispatch,
        getAvailability
      }
    }),
    [ibkState, configSkuJWT, preFilledFilters, dateFnsFunctions, getAvailability]
  );
  return <IbkContext.Provider value={value}>{children}</IbkContext.Provider>;
}
